import React, { useState, useEffect, useMemo } from 'react';
import cn from 'classnames';
import { AnimationOnScroll } from 'react-animation-on-scroll';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Image, { IImage } from 'tbk-components/src/components/Image';
import AnimatedArcs from '../../common/AnimatedArcs';

export interface ILocationSubpageHeroProps extends IBaseElementProps {
	/**
	 * Tagline text before the heading
	 * @control richtext
	 */
	tagline?: string;
	/**
	 * Heading text - bold text will have color gradient applied
	 * @control richtext
	 */
	heading?: string;
	/**
	 * CTA Button
	 */
	ctaButton?: ILink;
	/**
	 * Feature Image One - Recommended Size: 215px x 286px
	 */
	featureImageOne?: IImage;
	/**
	 * Feature Image Two - Recommended Size: 286px x 215px
	 */
	featureImageTwo?: IImage;
	/**
	 * Feature Image Three - Recommended Size: 215px x 286px
	 */
	featureImageThree?: IImage;
	disableAnimation?: boolean;
	/**
	 * Line Animation Delay in Milliseconds
	 */
	lineAnimationDelay?: number;
	/**
	 * Line Animation Offset in Pixels
	 */
	lineAnimationOffset?: number;
	/**
	 * Enable Desktop Picture Animations
	 */
	enableDesktopPictureAnimation?: boolean;
	/**
	 * Desktop Picture Animation Speed in Milliseconds
	 */
	desktopPictureAnimationSpeed?: number;
	/**
	 * Enable Tablet / Mobile Picture Animations
	 */
	enableMobilePictureAnimation?: boolean;
	/**
	 * Tablet / Mobile Picture Animation Speed in Milliseconds
	 */
	mobilePictureAnimationSpeed?: number;
}

/**
 * Location Subpage Hero
 */
const LocationSubpageHero: React.FC<ILocationSubpageHeroProps> = ({
	id,
	className,
	classNames = [],
	tagline,
	heading,
	ctaButton,
	featureImageOne,
	featureImageTwo,
	featureImageThree,
	disableAnimation = false,
	lineAnimationDelay = 1000,
	lineAnimationOffset = 150,
	enableDesktopPictureAnimation = false,
	desktopPictureAnimationSpeed = 15,
	enableMobilePictureAnimation = true,
	mobilePictureAnimationSpeed = 15,
}) => {
	// To help with https://gitlab.tbkdev.com/tbk-devs/calian.com/-/issues/632, where animation causes the
	// elements to not show up, we're going to use the initiallyVisible prop to show the animated elements
	// initially if we're in the block editor.
	const initiallyVisible = useMemo(() => {
		return document?.body?.classList.contains('block-editor-page');
	}, []);

	// Adding class to the full-width-template wrapper to apply gradient background styles. This useEffect approach was chosen to avoid having to create a new page template to accomodate the addition of this class name.
	useEffect(() => {
		const fullWidthTemplate = document.querySelector(
			'.full-width-template',
		);
		if (fullWidthTemplate) {
			fullWidthTemplate.classList.add('calian-location-inner');
		}
	}, []);

	const [pauseAnimationState, setPauseAnimationState] = useState(false);

	let pictureAnimationStyleVariables = {};

	if (desktopPictureAnimationSpeed) {
		pictureAnimationStyleVariables = {
			...pictureAnimationStyleVariables,
			'--desktop-picture-animation-speed': `${desktopPictureAnimationSpeed}s`,
		};
	}

	if (mobilePictureAnimationSpeed) {
		pictureAnimationStyleVariables = {
			...pictureAnimationStyleVariables,
			'--mobile-picture-animation-speed': `${mobilePictureAnimationSpeed}s`,
		};
	}

	return (
		<div
			id={id}
			className={
				className || cn('calian-location-subpage-hero', ...classNames)
			}
		>
			<div
				className="calian-location-subpage-hero__container container-fluid"
				style={pictureAnimationStyleVariables as React.CSSProperties}
			>
				<div className="row">
					<div className="calian-location-subpage-hero__left col-12 col-lg-7 col-xl-6 col-xxl-auto">
						<div className="calian-location-subpage-hero__content">
							{tagline && (
								<AnimationOnScroll
									animateIn={
										disableAnimation ? 'none' : 'fadeInUp'
									}
									animateOnce={true}
									delay={disableAnimation ? 0 : 500}
									initiallyVisible={initiallyVisible}
								>
									<p className="calian-location-subpage-hero__tagline">
										<span
											dangerouslySetInnerHTML={{
												__html: tagline,
											}}
										/>
									</p>
								</AnimationOnScroll>
							)}
							{heading && (
								<AnimationOnScroll
									animateIn={
										disableAnimation ? 'none' : 'fadeInUp'
									}
									animateOnce={true}
									delay={disableAnimation ? 0 : 750}
									initiallyVisible={initiallyVisible}
								>
									<h1 className="calian-location-subpage-hero__heading">
										<span
											dangerouslySetInnerHTML={{
												__html: heading,
											}}
										/>
									</h1>
								</AnimationOnScroll>
							)}
							{ctaButton?.label && (
								<AnimationOnScroll
									animateIn={
										disableAnimation ? 'none' : 'fadeInUp'
									}
									animateOnce={true}
									delay={disableAnimation ? 0 : 1000}
									initiallyVisible={initiallyVisible}
								>
									<Button
										className="btn btn-primary calian-location-subpage-hero__cta-button"
										{...ctaButton}
									/>
								</AnimationOnScroll>
							)}
						</div>
					</div>
					<div
						className={cn(
							'calian-location-subpage-hero__right',
							'col-12',
							'col-lg-5',
							'col-xl-6',
							'col-xxl-auto',
							'p-0',
							'px-lg-3',
							'd-flex',
							'flex-column',
							{
								'desktop-animation-enabled':
									enableDesktopPictureAnimation,
							},
							{
								'mobile-animation-enabled':
									enableMobilePictureAnimation,
							},
							{ 'animation-paused': pauseAnimationState },
							className,
						)}
					>
						<div
							className={cn(
								'calian-location-subpage-hero__images',
								'd-lg-flex',
								{
									'd-none': enableMobilePictureAnimation,
								},
								className,
							)}
						>
							{featureImageOne && (
								<AnimationOnScroll
									className="calian-location-subpage-hero__image-animation-container"
									animateIn={
										disableAnimation ? 'none' : 'fadeIn'
									}
									animateOnce={true}
									offset={-100}
									delay={disableAnimation ? 0 : 350}
									initiallyVisible={initiallyVisible}
								>
									<Image
										className="calian-location-subpage-hero__image-one img-fluid"
										{...featureImageOne}
									/>
								</AnimationOnScroll>
							)}
							{featureImageTwo && (
								<AnimationOnScroll
									className="calian-location-subpage-hero__image-animation-container"
									animateIn={
										disableAnimation ? 'none' : 'fadeIn'
									}
									animateOnce={true}
									offset={0}
									delay={disableAnimation ? 0 : 600}
									initiallyVisible={initiallyVisible}
								>
									<Image
										className="calian-location-subpage-hero__image-two img-fluid"
										{...featureImageTwo}
									/>
								</AnimationOnScroll>
							)}
							{featureImageThree && (
								<AnimationOnScroll
									className="calian-location-subpage-hero__image-animation-container"
									animateIn={
										disableAnimation ? 'none' : 'fadeIn'
									}
									animateOnce={true}
									offset={0}
									delay={disableAnimation ? 0 : 850}
									initiallyVisible={initiallyVisible}
								>
									<Image
										className="calian-location-subpage-hero__image-three img-fluid"
										{...featureImageThree}
									/>
								</AnimationOnScroll>
							)}
						</div>
						{enableDesktopPictureAnimation && (
							<div className="calian-location-subpage-hero__images marquee-double d-none d-lg-flex">
								{featureImageOne && (
									<AnimationOnScroll
										className="calian-location-subpage-hero__image-animation-container"
										animateIn={
											disableAnimation ? 'none' : 'fadeIn'
										}
										animateOnce={true}
										offset={-100}
										delay={disableAnimation ? 0 : 350}
										initiallyVisible={initiallyVisible}
									>
										<Image
											className="calian-location-subpage-hero__image-one img-fluid"
											{...featureImageOne}
										/>
									</AnimationOnScroll>
								)}
								{featureImageTwo && (
									<AnimationOnScroll
										className="calian-location-subpage-hero__image-animation-container"
										animateIn={
											disableAnimation ? 'none' : 'fadeIn'
										}
										animateOnce={true}
										offset={0}
										delay={disableAnimation ? 0 : 600}
										initiallyVisible={initiallyVisible}
									>
										<Image
											className="calian-location-subpage-hero__image-two img-fluid"
											{...featureImageTwo}
										/>
									</AnimationOnScroll>
								)}
								{featureImageThree && (
									<AnimationOnScroll
										className="calian-location-subpage-hero__image-animation-container"
										animateIn={
											disableAnimation ? 'none' : 'fadeIn'
										}
										animateOnce={true}
										offset={0}
										delay={disableAnimation ? 0 : 850}
										initiallyVisible={initiallyVisible}
									>
										<Image
											className="calian-location-subpage-hero__image-three img-fluid"
											{...featureImageThree}
										/>
									</AnimationOnScroll>
								)}
							</div>
						)}
						{enableMobilePictureAnimation && (
							<div className="calian-location-subpage-hero__images--marquee d-lg-none">
								<div className="calian-location-subpage-hero__images--marquee-content">
									{featureImageOne && (
										<AnimationOnScroll
											className="calian-location-subpage-hero__images--marquee-image"
											animateIn={
												disableAnimation
													? 'none'
													: 'fadeIn'
											}
											animateOnce={true}
											offset={-100}
											delay={disableAnimation ? 0 : 350}
											initiallyVisible={initiallyVisible}
										>
											<Image
												className=" img-fluid"
												{...featureImageOne}
											/>
										</AnimationOnScroll>
									)}
									{featureImageTwo && (
										<AnimationOnScroll
											className="calian-location-subpage-hero__images--marquee-image"
											animateIn={
												disableAnimation
													? 'none'
													: 'fadeIn'
											}
											animateOnce={true}
											offset={0}
											delay={disableAnimation ? 0 : 600}
											initiallyVisible={initiallyVisible}
										>
											<Image
												className=" img-fluid"
												{...featureImageTwo}
											/>
										</AnimationOnScroll>
									)}
									{featureImageThree && (
										<AnimationOnScroll
											className="calian-location-subpage-hero__images--marquee-image"
											animateIn={
												disableAnimation
													? 'none'
													: 'fadeIn'
											}
											animateOnce={true}
											offset={0}
											delay={disableAnimation ? 0 : 850}
											initiallyVisible={initiallyVisible}
										>
											<Image
												className=" img-fluid"
												{...featureImageThree}
											/>
										</AnimationOnScroll>
									)}
								</div>
								<div
									className="calian-location-subpage-hero__images--marquee-content"
									aria-hidden="true"
								>
									{featureImageOne && (
										<AnimationOnScroll
											className="calian-location-subpage-hero__images--marquee-image"
											animateIn={
												disableAnimation
													? 'none'
													: 'fadeIn'
											}
											animateOnce={true}
											offset={-100}
											delay={disableAnimation ? 0 : 350}
											initiallyVisible={initiallyVisible}
										>
											<Image
												className=" img-fluid"
												{...featureImageOne}
											/>
										</AnimationOnScroll>
									)}
									{featureImageTwo && (
										<AnimationOnScroll
											className="calian-location-subpage-hero__images--marquee-image"
											animateIn={
												disableAnimation
													? 'none'
													: 'fadeIn'
											}
											animateOnce={true}
											offset={0}
											delay={disableAnimation ? 0 : 600}
											initiallyVisible={initiallyVisible}
										>
											<Image
												className=" img-fluid"
												{...featureImageTwo}
											/>
										</AnimationOnScroll>
									)}
									{featureImageThree && (
										<AnimationOnScroll
											className="calian-location-subpage-hero__images--marquee-image"
											animateIn={
												disableAnimation
													? 'none'
													: 'fadeIn'
											}
											animateOnce={true}
											offset={0}
											delay={disableAnimation ? 0 : 850}
											initiallyVisible={initiallyVisible}
										>
											<Image
												className=" img-fluid"
												{...featureImageThree}
											/>
										</AnimationOnScroll>
									)}
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
			<div className="calian-location-subpage-hero__pause-btn-container position-absolute z-3">
				<a
					href="#/"
					className="calian-location-subpage-hero__pause-btn btn btn-sm p-1 d-flex justify-center align-items-center fs-8"
					onClick={() => setPauseAnimationState(!pauseAnimationState)}
				>
					{/* If pauseAnimationState is true show one icon if false show another */}
					{pauseAnimationState ? (
						<>
							<span className="icon icon-play-arrow fs-9 me-1 pe-1"></span>
							Play
						</>
					) : (
						<>
							<span className="icon icon-pause fs-9 me-1 pe-1"></span>
							Pause
						</>
					)}
				</a>
			</div>
			<AnimatedArcs
				arcsStyle={'Hero - Three Lines'}
				arcsPosition="Right"
				arcOneColor="var(--bs-white)"
				arcOneStrokeWidth={4}
				arcTwoColor="var(--bs-primary)"
				arcTwoStrokeWidth={3}
				arcThreeColor="var(--bs-white)"
				arcThreeStrokeWidth={2}
				animationDelay={lineAnimationDelay}
				animationOffset={lineAnimationOffset}
				disableAnimation={disableAnimation}
			/>
		</div>
	);
};

export default LocationSubpageHero;
